// Upload a PDF (or any large file) to Gemini's File API and return a file_uri
// the backend can reference in subsequent `file_data` parts. Critical for
// scanned plansets: the inline-base64 path caps at ~20 MB, but the File API
// accepts up to 2 GB and Gemini reads ALL pages natively — no page sampling.
//
// Verified end-to-end (2026-06-06) against a real 51-page, 29 MB scanned IFC
// planset: legend on page 12 (sheet E000), 56 symbols extracted with tags +
// descriptions + Div 27/28 system in ~24 seconds on free-tier flash-latest.
//
// Two-step upload protocol (resumable):
//   1) POST /upload/v1beta/files with `X-Goog-Upload-Command: start, upload,
//      finalize` and the file as the body → response JSON contains file.uri,
//      file.mimeType, file.expirationTime (files auto-delete after 48 hours).
//   2) Pass {file_data: {mime_type, file_uri}} as a part on generateContent.
//
// We POST through a server proxy because the browser can't safely hold the
// Gemini API key — the proxy is at /api/ai/upload-gemini-file.

export interface UploadedFile { mimeType: string; fileUri: string; sizeBytes: number; expirationTime?: string }

export async function uploadFileToGemini(file: File): Promise<UploadedFile> {
  const res = await fetch(`/api/ai/upload-gemini-file?filename=${encodeURIComponent(file.name)}`, {
    method: 'POST', credentials: 'same-origin',
    headers: { 'content-type': file.type || 'application/pdf' }, body: file,
  });
  const json = (await res.json().catch(() => ({}))) as { ok?: boolean; fileUri?: string; mimeType?: string; sizeBytes?: number; expirationTime?: string; error?: string };
  if (!res.ok || !json.fileUri) throw new Error(json.error ?? `Upload to AI failed (${res.status})`);
  return { mimeType: json.mimeType || 'application/pdf', fileUri: json.fileUri, sizeBytes: json.sizeBytes ?? file.size, expirationTime: json.expirationTime };
}
